import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { eventsService } from '../services/eventsService';
import { segmentService } from '../services/segmentService';
import { analyticsService } from '../services/analyticsService';
import { formatCompactNumber, formatRelativeTime } from '../utils/format';
import Skeleton from '../components/ui/Skeleton';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import EventBus from '../utils/EventBus';
import styles from './Events.module.css';

const PERIODS = [
  { key: 7, label: '7d' },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

const LOG_LIMIT = 50;
const MAX_FUNNEL_STEPS = 5;

// Custom dates (both set) win over the preset period — matches this
// codebase's other date-range endpoints ({from,to} ISO strings).
//
// `new Date('2026-07-15')` (date-only, no time) parses as UTC midnight per
// spec, but `to`'s old setHours(23,59,59,999) mutates in the *local*
// timezone — so `from` stayed pinned to UTC midnight while `to` shifted to
// local end-of-day, silently excluding the first few hours of the picked
// start date for anyone east of UTC (e.g. IST misses 12:00am-5:30am on
// `customFrom`). Appending an explicit local time-of-day (no Z/offset) makes
// both bounds parse in the browser's local timezone instead, matching what
// an admin picking a date actually means by "that whole day".
function periodRange(period, customFrom, customTo) {
  if (customFrom && customTo) {
    const from = new Date(customFrom + 'T00:00:00');
    const to = new Date(customTo + 'T23:59:59.999');
    return { from: from.toISOString(), to: to.toISOString() };
  }
  const to = new Date();
  const from = new Date(to.getTime() - period * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

class Events extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      period: 30,
      customFrom: '',
      customTo: '',
      eventName: '',
      eventOptions: [],
      propertyKey: '',
      propertyValue: '',
      statsLoading: true,
      stats: null,
      properties: null,
      logLoading: true,
      logRows: [],
      logTotal: 0,
      logPage: 1,
      expandedRow: null,
      funnelSteps: ['', ''],
      funnelLoading: false,
      funnelResult: null,
    };
    this.loadStats = this.loadStats.bind(this);
    this.loadLog = this.loadLog.bind(this);
    this.runFunnel = this.runFunnel.bind(this);
  }

  componentDidMount() {
    segmentService
      .getOptions()
      .then(({ events }) => this.setState({ eventOptions: (events || []).filter((e) => e.table === 'events') }))
      .catch(() => {});
    this.loadStats();
    this.loadLog(1);
  }

  componentDidUpdate(prevProps, prevState) {
    const { period, eventName, customFrom, customTo, propertyKey, propertyValue } = this.state;
    if (
      prevState.period !== period ||
      prevState.eventName !== eventName ||
      prevState.customFrom !== customFrom ||
      prevState.customTo !== customTo ||
      prevState.propertyKey !== propertyKey ||
      prevState.propertyValue !== propertyValue
    ) {
      this.loadStats();
      this.loadLog(1);
    }
    if (prevState.eventName !== eventName) this.loadProperties();
  }

  selectPeriod(days) {
    // Typing a custom date implicitly wins over presets already — clear it
    // here so choosing a preset doesn't leave a contradictory-looking UI.
    this.setState({ period: days, customFrom: '', customTo: '' });
  }

  loadStats() {
    const { period, customFrom, customTo, eventName, propertyKey, propertyValue } = this.state;
    const { from, to } = periodRange(period, customFrom, customTo);
    this.setState({ statsLoading: true });
    eventsService
      .stats({ from, to, event_name: eventName || undefined, property_key: propertyKey || undefined, property_value: propertyValue || undefined })
      .then((stats) => this.setState({ statsLoading: false, stats }))
      .catch((err) => {
        this.setState({ statsLoading: false, stats: null });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load event stats.' });
      });
  }

  loadProperties() {
    const { eventName } = this.state;
    if (!eventName) return this.setState({ properties: null });
    segmentService
      .getEventProperties('events', eventName)
      .then((properties) => this.setState({ properties }))
      .catch(() => this.setState({ properties: null }));
  }

  loadLog(page) {
    const { period, customFrom, customTo, eventName, propertyKey, propertyValue } = this.state;
    const { from, to } = periodRange(period, customFrom, customTo);
    this.setState({ logLoading: true });
    eventsService
      .log({
        from,
        to,
        event_name: eventName || undefined,
        property_key: propertyKey || undefined,
        property_value: propertyValue || undefined,
        page,
        limit: LOG_LIMIT,
      })
      .then(({ data, meta }) =>
        this.setState((s) => ({
          logLoading: false,
          logPage: page,
          logTotal: meta.total,
          logRows: page === 1 ? data : s.logRows.concat(data),
        }))
      )
      .catch((err) => {
        this.setState({ logLoading: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load event log.' });
      });
  }

  setFunnelStep(i, value) {
    this.setState((s) => {
      const steps = s.funnelSteps.slice();
      steps[i] = value;
      return { funnelSteps: steps };
    });
  }

  addFunnelStep() {
    this.setState((s) => (s.funnelSteps.length >= MAX_FUNNEL_STEPS ? s : { funnelSteps: s.funnelSteps.concat('') }));
  }

  removeFunnelStep(i) {
    this.setState((s) => (s.funnelSteps.length <= 2 ? s : { funnelSteps: s.funnelSteps.filter((_, idx) => idx !== i) }));
  }

  runFunnel() {
    const steps = this.state.funnelSteps.filter(Boolean);
    if (steps.length < 2) {
      EventBus.emit('toast', { type: 'error', message: 'Pick at least 2 events for the funnel.' });
      return;
    }
    this.setState({ funnelLoading: true });
    analyticsService
      .funnel(steps)
      .then((funnelResult) => this.setState({ funnelLoading: false, funnelResult }))
      .catch((err) => {
        this.setState({ funnelLoading: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to compute funnel.' });
      });
  }

  render() {
    const {
      period, customFrom, customTo, eventName, eventOptions, propertyKey, propertyValue,
      statsLoading, stats, properties,
      logLoading, logRows, logTotal, logPage, expandedRow,
      funnelSteps, funnelLoading, funnelResult,
    } = this.state;

    const daily = (stats?.daily || []).map((r) => ({
      day: new Date(r.day).toLocaleDateString([], { month: 'short', day: 'numeric' }),
      count: Number(r.count),
    }));
    const topEvents = (stats?.top_events || []).map((r) => ({ l: r.name.replace(/_/g, ' '), name: r.name, v: Number(r.count) }));
    const hasMore = logRows.length < logTotal;
    const funnelMax = Math.max(1, funnelResult?.[0]?.count || 0);

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Events</div>
            <div className={styles.pageSub}>Raw event stream from `/events/capture` · segment/DAU-WAU source data</div>
          </div>
          <div className={styles.controls}>
            <Select
              style={{ width: 200 }}
              value={eventName}
              options={[{ value: '', label: 'All events' }, ...eventOptions.map((e) => ({ value: e.name, label: e.name }))]}
              onChange={(val) => this.setState({ eventName: val })}
            />
            <div className={styles.periodGroup}>
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  className={[styles.periodBtn, period === p.key && !customFrom && !customTo ? styles.periodOn : ''].join(' ')}
                  onClick={() => this.selectPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className={styles.dateRange}>
              <Input
                type="date"
                style={{ width: 150 }}
                value={customFrom}
                onChange={(e) => this.setState({ customFrom: e.target.value })}
              />
              <span className={styles.dateSep}>–</span>
              <Input
                type="date"
                style={{ width: 150 }}
                value={customTo}
                onChange={(e) => this.setState({ customTo: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className={styles.propFilterRow}>
          <span className={styles.propFilterLabel}>Property filter</span>
          <Input
            style={{ width: 160 }}
            placeholder="key (e.g. status)"
            value={propertyKey}
            onChange={(e) => this.setState({ propertyKey: e.target.value })}
          />
          <Input
            style={{ width: 160 }}
            placeholder="value (e.g. success)"
            value={propertyValue}
            onChange={(e) => this.setState({ propertyValue: e.target.value })}
          />
          {(propertyKey || propertyValue) && (
            <button className={styles.propFilterClear} onClick={() => this.setState({ propertyKey: '', propertyValue: '' })}>
              Clear
            </button>
          )}
        </div>

        {statsLoading || !stats ? (
          <Skeleton variant="rect" width="100%" height={90} style={{ borderRadius: 12, marginBottom: 18 }} />
        ) : (
          <div className={styles.statRow}>
            <div className={styles.statCard}>
              <div className={styles.statVal}>{formatCompactNumber(stats.total)}</div>
              <div className={styles.statLbl}>Total events</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statVal}>{formatCompactNumber(stats.unique_users)}</div>
              <div className={styles.statLbl}>Unique users</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statVal}>{topEvents[0]?.name || (eventName || '—')}</div>
              <div className={styles.statLbl}>{eventName ? 'Selected event' : 'Most frequent event'}</div>
            </div>
          </div>
        )}

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Volume</div>
            <div className={styles.cardSub}>Daily count · {customFrom && customTo ? `${customFrom} to ${customTo}` : `last ${period} days`}</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={daily} margin={{ top: 8, right: 16, left: -20, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="count" name="Events" stroke="#C09848" fill="#C0984822" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className={styles.grid2}>
          {!eventName && (
            <div className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardTitle}>Top events</div>
                <div className={styles.cardSub}>Click a bar to drill in</div>
              </div>
              <ResponsiveContainer width="100%" height={215}>
                <BarChart data={topEvents} layout="vertical" margin={{ top: 0, right: 8, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="l" type="category" tick={{ fill: 'var(--t2)', fontSize: 10 }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                  <Bar
                    dataKey="v"
                    name="Events"
                    fill="var(--red)"
                    radius={[0, 3, 3, 0]}
                    onClick={(d) => this.setState({ eventName: d?.payload?.name || d?.name })}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {eventName && (
            <div className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardTitle}>Common properties</div>
                <div className={styles.cardSub}>Discovered live from recent "{eventName}" events</div>
              </div>
              <div className={styles.propList}>
                {!properties || properties.length === 0 ? (
                  <div className={styles.cardSub} style={{ padding: 16 }}>No properties recorded on this event.</div>
                ) : (
                  properties.map((p) => {
                    const samples = p.sample_values || [];
                    return (
                      <div
                        key={p.name}
                        className={[styles.propRow, styles.propRowClickable].join(' ')}
                        title="Click to filter the log/stats by this property"
                        onClick={() => this.setState({ propertyKey: p.name, propertyValue: samples.length === 1 ? samples[0] : '' })}
                      >
                        <span className={styles.propName}>{p.name}</span>
                        <span className={styles.propSamples}>{samples.join(', ')}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Event log</div>
            <div className={styles.cardSub}>{formatCompactNumber(logTotal)} matching events</div>
          </div>
          <div className={styles.logTable}>
            <div className={styles.logHeadRow}>
              <div className={styles.logColTime}>Time</div>
              <div className={styles.logColUser}>User ID</div>
              <div className={styles.logColEvent}>Event</div>
            </div>
            {logRows.map((row, i) => {
              const key = row.user_id + row.event_time + i;
              const expanded = expandedRow === key;
              return (
                <div key={key} className={styles.logRowWrap}>
                  <div className={styles.logRow} onClick={() => this.setState({ expandedRow: expanded ? null : key })}>
                    <div className={styles.logColTime} title={row.event_time}>{formatRelativeTime(row.event_time)}</div>
                    <div className={styles.logColUser}>{row.user_id}</div>
                    <div className={styles.logColEvent}>{row.event_name}</div>
                  </div>
                  {expanded && (
                    <pre className={styles.propertiesJson}>{JSON.stringify(row.properties, null, 2)}</pre>
                  )}
                </div>
              );
            })}
            {!logLoading && logRows.length === 0 && (
              <div className={styles.cardSub} style={{ padding: 16 }}>No events in this window.</div>
            )}
          </div>
          {hasMore && (
            <div className={styles.loadMoreWrap}>
              <button className={styles.loadMoreBtn} disabled={logLoading} onClick={() => this.loadLog(logPage + 1)}>
                {logLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Event funnel</div>
            <div className={styles.cardSub}>All-time · each user's own 30-day window from their first step-1 event · independent of the filters above</div>
          </div>
          <div className={styles.funnelBuilder}>
            {funnelSteps.map((step, i) => (
              <div key={i} className={styles.funnelStepRow}>
                <span className={styles.funnelStepNum}>{i + 1}</span>
                <Select
                  style={{ width: 200 }}
                  value={step}
                  placeholder="Select event…"
                  options={eventOptions.map((e) => ({ value: e.name, label: e.name }))}
                  onChange={(val) => this.setFunnelStep(i, val)}
                />
                {funnelSteps.length > 2 && (
                  <button className={styles.funnelStepRemove} onClick={() => this.removeFunnelStep(i)}>×</button>
                )}
              </div>
            ))}
            <div className={styles.funnelActions}>
              {funnelSteps.length < MAX_FUNNEL_STEPS && (
                <button className={styles.loadMoreBtn} onClick={() => this.addFunnelStep()}>+ Add step</button>
              )}
              <button className={styles.propFilterClear} disabled={funnelLoading} onClick={this.runFunnel}>
                {funnelLoading ? 'Running…' : 'Run funnel'}
              </button>
            </div>
          </div>

          {funnelResult && (
            <div className={styles.funnelWrap}>
              {funnelResult.map((s, i) => {
                const pct = Math.round((s.count / funnelMax) * 100);
                const col = ['#686868', '#4878B0', '#C09848', '#8878C0', '#4F9B6F'][i % 5];
                return (
                  <div key={s.step} className={styles.funnelRow}>
                    <div className={styles.funnelBar} style={{ width: pct + '%', background: col + '22', borderLeft: '3px solid ' + col }} />
                    <div className={styles.funnelLabel}>{s.event}</div>
                    <div className={styles.funnelCount} style={{ color: col }}>{formatCompactNumber(s.count)}</div>
                    <div className={styles.funnelPct}>{s.conversion_rate}%</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  }
}

export default Events;
